import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text
} from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import {
  useDeleteDataset,
  useInvalidateDatasetQueries
} from '@/queries/datasetQueries'
import { showToast } from '@/theme/notifications'

const RESTORE_DAYS = 30

type DeleteDatasetModalProps = {
  opened: boolean
  datasetId?: string
  onClose: () => void
}

export const DeleteDatasetModal = ({
  opened,
  datasetId,
  onClose
}: DeleteDatasetModalProps) => {
  const intl = useIntl()

  const { isPending, mutateAsync: deleteDataset } = useDeleteDataset()

  const { invalidateDatasetList } = useInvalidateDatasetQueries()

  const handleDatasetDelete = async () => {
    if (!datasetId) {
      return
    }

    try {
      await deleteDataset(datasetId)

      showToast(
        intl.formatMessage({ id: 'datasets.delete.success' }),
        'success'
      )

      void invalidateDatasetList()
      onClose()
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage || intl.formatMessage({ id: 'datasets.delete.error' })

      showToast(message, 'error')
    }
  }

  return (
    <Modal
      size="lg"
      opened={opened}
      title={<FormattedMessage id="datasets.delete" />}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Stack gap="xl">
          <Text size="sm">
            <FormattedMessage id="datasets.delete.confirmation" />
          </Text>

          <Text size="sm">
            <FormattedMessage
              id="datasets.delete.restore"
              values={{
                days: RESTORE_DAYS
              }}
            />
          </Text>

          <Group justify="end">
            <Button miw={120} variant="default" onClick={onClose}>
              <FormattedMessage id="cancel" />
            </Button>

            <Button
              miw={120}
              color="red"
              onClick={() => void handleDatasetDelete()}
            >
              <FormattedMessage id="delete" />
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
